// 全局自动备份提醒条（挂在 AppLayout 顶部，所有页面可见）
// 行为（自动备份开启时）：
//   距上次备份超 24h：
//     已绑定阿里云盘 → 静默自动上传备份（绿色提示）
//     未绑定云盘     → 每天最多提醒一次（黄色条，引导去备份）
//   已绑定飞牛 NAS（WebDAV，支持多台）→ 独立于 24h：打开时逐台探测，在线即补推最新备份（绿色提示），离线静默跳过
//   已绑定七牛云 Kodo → 独立于 24h：打开时自动补推最新备份（固定文件名覆盖式，幂等）
//   多通道互不阻塞：云盘成功不跳过飞牛/七牛，飞牛/七牛成功不覆盖云盘消息
import { useEffect, useState } from 'react';
import { ArchiveRestore, CloudUpload, X, AlertTriangle, CheckCircle2, Server } from 'lucide-react';
import {
  shouldRemindBackup,
  isAutoBackupOn,
  markBackupDone,
  collectBackup,
  backupFileName,
  getAllWebDavConfigs,
  lastWebDavSyncAt,
  pushToWebDav,
  WEBDAV_SYNC_INTERVAL_MS,
  WEBDAV_LATEST_NAME,
  getQiniuConfig,
  lastQiniuSyncAt,
  pushToQiniu,
  QINIU_SYNC_INTERVAL_MS,
  QINIU_LATEST_NAME,
} from '@/services/backupService';
import { getValidToken, getDriveInfo, uploadBackup, cleanupOldBackups } from '@/lib/aliyunpan';
import { probeWebDavOnline } from '@/lib/webdav';

const REMIND_DATE_KEY = 'changfa_backup_remind_date';

interface Props {
  onNavigate: (page: string, childId?: string) => void;
}

type UiState = 'none' | 'remind' | 'auto-done' | 'auto-err';

export function BackupAutoNotify({ onNavigate }: Props) {
  const [state, setState] = useState<UiState>('none');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!isAutoBackupOn()) return;
        let shown = false;

        // ===== A. 云盘自动上传 / 每天提醒（超 24h 触发）=====
        if (shouldRemindBackup()) {
          const token = await getValidToken();
          if (token) {
            try {
              if (!token.driveId) {
                const info = await getDriveInfo(token);
                token.driveId = info.default_drive_id;
              }
              const backup = await collectBackup();
              const name = backupFileName();
              const { name: finalName } = await uploadBackup(token, token.driveId!, name, JSON.stringify(backup));
              await cleanupOldBackups(token, token.driveId!);
              markBackupDone('aliyunpan', finalName);
              if (!cancelled) {
                shown = true;
                setMsg(`已自动备份到阿里云盘「昌发工作台备份」：${finalName}`);
                setState('auto-done');
              }
            } catch (e) {
              if (!cancelled) {
                shown = true;
                setMsg(`自动备份到云盘失败：${e instanceof Error ? e.message : '未知错误'}，请手动导出或重新授权`);
                setState('auto-err');
              }
            }
          } else {
            // 未绑定云盘 → 每天提醒一次
            // 注意：StrictMode 开发模式会双挂载，先检查标记再写入，
            // 避免第一个实例写入标记后第二个实例直接 return（都不渲染）
            if (cancelled) return;
            try {
              const today = new Date().toDateString();
              if (localStorage.getItem(REMIND_DATE_KEY) === today) return;
            } catch { /* Best-effort operation; failure is non-fatal. */ }
            shown = true;
            setState('remind');
            try {
              localStorage.setItem(REMIND_DATE_KEY, new Date().toDateString());
            } catch { /* Best-effort operation; failure is non-fatal. */ }
          }
        }

        // ===== B. 飞牛 NAS 智能同步（独立间隔，支持多台，NAS 不常开 → 打开即探测补推）=====
        // StrictMode 双挂载说明：两个实例都会执行本段，但自动同步用「固定文件名」上传
        // （覆盖式，天然幂等），重复执行只会覆盖同一文件；展示由未取消的实例负责。
        // 多台飞牛并行探测：在线即补推，离线静默跳过；A 段已展示则不覆盖其消息。
        const wdList = getAllWebDavConfigs();
        const wdSyncables = wdList.filter((w) => w?.autoSync);
        if (wdSyncables.length > 0 && Date.now() - lastWebDavSyncAt() >= WEBDAV_SYNC_INTERVAL_MS) {
          const wdResults = await Promise.all(
            wdSyncables.map(async (w) => {
              try {
                const online = await probeWebDavOnline(w, 3000);
                if (!online) return null; // 离线 → 静默跳过，不打扰
                // 固定名「昌发工作台最新备份.json」：多次打开/双挂载不会累积文件
                const res = await pushToWebDav(w, WEBDAV_LATEST_NAME);
                return { host: w.url.replace(/^https?:\/\//, ''), dir: w.dir, ok: res.ok, message: res.message };
              } catch {
                return null; // 网络/解析异常 → 静默，不打扰
              }
            })
          );
          const synced = wdResults.filter((r): r is NonNullable<typeof r> => r !== null && r.ok);
          const failed = wdResults.filter((r): r is NonNullable<typeof r> => r !== null && !r.ok);
          if ((synced.length > 0 || failed.length > 0) && !cancelled && !shown) {
            if (failed.length === 0) {
              setState('auto-done');
              setMsg(
                synced.length > 1
                  ? `已同步到 ${synced.length} 台飞牛 NAS（${synced.map((r) => r.host).join('、')}）`
                  : `已同步到飞牛 NAS「${synced[0].dir}」：${WEBDAV_LATEST_NAME}`
              );
            } else if (synced.length > 0) {
              setState('auto-err');
              setMsg(`飞牛同步：${synced.length} 台成功，${failed.length} 台失败（${failed.map((f) => `${f.host}：${f.message}`).join('；')}）`);
            } else {
              setState('auto-err');
              setMsg(`同步到飞牛 NAS 失败：${failed.map((f) => `${f.host}（${f.message}）`).join('；')}`);
            }
          }
        }

        // ===== C. 七牛云自动同步（独立间隔，Edge Function 在线即补推）=====
        // 与飞牛 B 段同样的覆盖式幂等策略：固定文件名上传，重复执行只覆盖同一文件
        const qcfg = getQiniuConfig();
        if (qcfg && Date.now() - lastQiniuSyncAt() >= QINIU_SYNC_INTERVAL_MS) {
          try {
            const res = await pushToQiniu(qcfg, QINIU_LATEST_NAME);
            if (res.ok && !cancelled && !shown) {
              shown = true;
              setState('auto-done');
              setMsg(`已同步到七牛云「${qcfg.bucket}」：${QINIU_LATEST_NAME}`);
            }
          } catch {
            // 静默跳过，不打扰
          }
        }
      } catch {
        // 忽略异常，不打扰用户
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'none') return null;

  const dismiss = () => setState('none');

  const goBackup = () => {
    dismiss();
    onNavigate('system', 'system-backup');
  };

  if (state === 'auto-done') {
    return (
      <div className="bg-emerald-600 text-white text-[12.5px] px-4 py-2 flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {msg.includes('飞牛') || msg.includes('七牛') ? <Server className="w-4 h-4 flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
          <span className="truncate">{msg}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button onClick={goBackup} className="underline underline-offset-2 hover:opacity-80">查看</button>
          <button onClick={dismiss} aria-label="关闭"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className={state === 'auto-err' ? 'bg-rose-500 text-white text-[12.5px] px-4 py-2 flex items-center gap-2 justify-between' : 'bg-amber-400 text-amber-950 text-[12.5px] px-4 py-2 flex items-center gap-2 justify-between'}>
      <div className="flex items-center gap-2 min-w-0">
        {state === 'remind' ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <CloudUpload className="w-4 h-4 flex-shrink-0" />}
        <span className="truncate">
          {state === 'remind' ? '距上次备份已超过 24 小时，建议立即备份，防止系统崩溃丢失数据' : msg}
        </span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={goBackup}
          className={cnBtn(state)}
        >
          <ArchiveRestore className="w-3.5 h-3.5" /> 去备份
        </button>
        {state === 'remind' && <button onClick={dismiss} className="opacity-70 hover:opacity-100" aria-label="今日不再提醒">今天不提醒</button>}
        <button onClick={dismiss} aria-label="关闭"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function cnBtn(state: UiState): string {
  return state === 'auto-err'
    ? 'flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/20 hover:bg-white/30 font-medium'
    : 'flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-950 text-amber-50 hover:bg-amber-900 font-medium';
}
